import React, { useRef, useState } from 'react';
import { 
  Download, 
  Copy, 
  Check, 
  User, 
  ShieldCheck, 
  Sparkles, 
  Award, 
  Code2, 
  Briefcase, 
  Terminal, 
  Globe, 
  ExternalLink,
  Layers,
  Cpu,
  Mail,
  Calendar,
  CheckCircle2
} from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas-pro';
import { sanitizeClonedDocForPdf } from '../utils/pdfColorSanitizer';

interface FounderProfileDocumentProps {
  showToast?: (msg: string, type?: 'success' | 'info') => void;
}

export function FounderProfileDocument({ showToast }: FounderProfileDocumentProps) {
  const [copied, setCopied] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const plainTextProfile = `=====================================================
AZURA SERAPH™ // AZURAOS — 1-PAGE FOUNDER PROFILE
Founder & Principal Architect: Raksha Singh
Direct: founder@azuraos.app | iwillriseupstudio@gmail.com
Live Platform: https://azuraos.app | Company: Azura Seraph™ Pty Ltd
Status: 100% Bootstrapped Since 2023 • Clean Cap Table (Zero Debt)
=====================================================

1. EXECUTIVE SUMMARY
Raksha Singh is the Founder and Principal Architect of Azura Seraph™ Pty Ltd and AzuraOS. Operating as a sovereign technical founder, she has engineered and shipped AzuraOS from the ground up—combining systems analysis rigor, luxury operational standards, and deep reactive software engineering into an air-gapped, institutional-grade financial operating system for autonomous founders, micro-enterprises, and accelerator cohorts.

2. FOUNDER ARCHETYPE & CROSS-DISCIPLINARY BACKGROUND
• Systems Analysis & Precision Engineering: Rigorous protocol design, fault tolerance, and unyielding discipline where operational failure is not an option.
• Luxury Operations & Executive Experience: Understanding white-glove user psychology, high-stakes decision workflows, and eliminating cognitive friction for busy founders.
• Full-Stack Software Engineering: Sole author and architect of the entire AzuraOS codebase—from the client-side cryptographic vault (Web Cryptography AES-GCM) and in-memory WebAssembly bank statement parser to the multi-jurisdiction tax engine (IRS, HMRC, EU VAT, SARS, ATO) and multimodal voice synthesis chambers.

3. PROVEN BOOTSTRAPPING TRACTION & VALIDATION
• Live Production Deployment: Live in production at https://azuraos.app. Azura Seraph™ runs 100% of its treasury and enterprise operations on this kernel.
• Google for Startups Cloud Program: Start Tier recipient ($2,000 developer cloud infrastructure grant).
• Google Cloud Partner Advantage: Formally validated ecosystem partner.
• AltaLab Sprint Cohort 2026: Selected for active founder sprint.
• Slush 2026 (Helsinki): Accepted startup founder profile alongside top 25 global VCs.
• WIT-h Network: Formally inducted into the global Women in Tech network.
• Private Tester Feedback: 15 active operators and SaaS founders stress-testing ledger reconciliation, pilot SLAs, and solvency calculations.

4. CORE ARCHITECTURAL & TECHNICAL EXPERTISE
• Frontend & Local-First Security: React 19, TypeScript, Vite, Tailwind CSS, Web Cryptography API (AES-GCM client-side encryption), WebAssembly/IndexedDB.
• Privacy-Preserving Parser: Sub-50ms client-side statement ingestion with zero third-party bank credential harvesting (anti-Plaid liability).
• Backend & Cloud Infrastructure: Node.js, Express, Firebase Firestore & Authentication, Google Cloud Run microservices.
• AI & Multimodal Intelligence: Google Gemini 2.5/3 Flash & Pro via @google/genai SDK, BYOK multi-provider routing (Claude 3.5 Sonnet, GPT-4o), structured tool calling, and Multimodal Live API.
• Global Payments & Compliance: Dodo Payments Merchant of Record integration enabling tax-compliant checkouts across 150+ countries.

5. COMMERCIAL MODEL & UNIT ECONOMICS
• B2B Cohort Wedge: $750 for 30-day cohort pilot (25 founder seats + Director governance portal).
• Core SaaS Retention: $29/mo ($290/yr) Solo Founder tier, $79/mo Pro Multi-Entity tier.
• Financial Moat: >85% gross margins achieved via zero server-side database scraping, local-first computing, and BYOK AI routing.
• Capital Efficiency: LTV:CAC ratio estimated at ~7.6x through programmatic B2B2C cohort acquisition.

6. PERSONAL MISSION
"To empower the next generation of autonomous founders and 1-person companies with sovereign, audit-ready operational infrastructure that prevents cash-flow exhaustion before money leaves the bank."
=====================================================`;

  const handleCopy = () => {
    navigator.clipboard.writeText(plainTextProfile);
    setCopied(true);
    if (showToast) showToast('📋 1-Page Founder Profile copied to clipboard!', 'success');
    setTimeout(() => setCopied(false), 2500);
  };

  const handleDownloadPdf = async () => {
    setIsGeneratingPdf(true);
    try {
      const pageEl = document.getElementById('azura-founder-profile-a4-page');
      if (!pageEl) throw new Error('Founder Profile element not found');

      const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
        import('jspdf'),
        import('html2canvas-pro')
      ]);

      const canvas = await html2canvas(pageEl, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        onclone: (clonedDoc) => {
          sanitizeClonedDocForPdf(clonedDoc);
          const wrapper = clonedDoc.getElementById('azura-founder-profile-pdf-wrapper');
          if (wrapper) {
            wrapper.style.position = 'static';
            wrapper.style.left = '0';
            wrapper.style.top = '0';
            wrapper.style.display = 'block';
            wrapper.style.visibility = 'visible';
            wrapper.style.opacity = '1';
          }
          const p = clonedDoc.getElementById('azura-founder-profile-a4-page');
          if (p) {
            p.style.position = 'static';
            p.style.visibility = 'visible';
            p.style.opacity = '1';
          }
        }
      });

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', 0, 0, 210, 297, undefined, 'FAST');
      pdf.save('Raksha_Singh_Azura_Seraph_Founder_Profile.pdf');

      if (showToast) showToast('✅ 1-Page Founder Profile PDF downloaded (A4 calibrated)!', 'success');
    } catch (err) {
      console.error('Founder Profile PDF export error:', err);
      if (showToast) showToast('⚠️ Direct PDF export issue, opening print view...', 'info');
      window.print();
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Action Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 bg-[#101314] border border-[#242A2D] rounded-2xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-400 flex items-center justify-center shrink-0">
            <User size={20} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-extrabold text-white tracking-tight">
                1-Page Founder Profile • Raksha Singh
              </h3>
              <span className="px-2 py-0.5 text-[9px] font-mono font-extrabold bg-amber-500/20 text-amber-300 border border-amber-500/40 rounded">
                FOUNDER &amp; ARCHITECT
              </span>
            </div>
            <p className="text-xs text-gray-400 mt-0.5">
              Verified executive dossier, technical capabilities, and ecosystem validation ready for diligence &amp; accelerators.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleCopy}
            className="px-3.5 py-2 bg-[#1A1E20] hover:bg-[#252A2D] border border-gray-700 hover:border-gray-500 text-xs font-bold rounded-xl text-gray-200 hover:text-white transition flex items-center gap-1.5 cursor-pointer"
          >
            {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} className="text-amber-400" />}
            <span>{copied ? 'Copied Profile!' : 'Copy Text'}</span>
          </button>

          <button
            onClick={handleDownloadPdf}
            disabled={isGeneratingPdf}
            className="px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-extrabold text-xs rounded-xl shadow-lg transition flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
          >
            <Download size={14} />
            <span>{isGeneratingPdf ? 'Generating PDF...' : 'Download PDF (A4)'}</span>
          </button>
        </div>
      </div>

      {/* Screen Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-5 bg-gradient-to-b from-[#141718] to-[#0E1011] border border-amber-500/30 rounded-2xl space-y-3">
          <div className="flex items-center gap-2 text-amber-400 font-bold text-xs uppercase tracking-wider">
            <Cpu size={15} />
            <span>Sole Technical Architect</span>
          </div>
          <div className="text-white text-sm font-semibold">
            End-to-End System Authorship
          </div>
          <p className="text-xs text-gray-400 leading-relaxed">
            Engineered the entire AzuraOS core: client-side cryptographic vault, sub-50ms statement parser, global multi-jurisdiction tax engine, and cohort portal.
          </p>
        </div>

        <div className="p-5 bg-gradient-to-b from-[#141718] to-[#0E1011] border border-teal-500/30 rounded-2xl space-y-3">
          <div className="flex items-center gap-2 text-teal-400 font-bold text-xs uppercase tracking-wider">
            <Award size={15} />
            <span>Ecosystem Validation</span>
          </div>
          <div className="text-white text-sm font-semibold">
            Google for Startups &amp; Partners
          </div>
          <p className="text-xs text-gray-400 leading-relaxed">
            Google for Startups Cloud Program grant ($2,000), Google Cloud Partner Advantage member, AltaLab Sprint 2026, and Slush 2026 showcase acceptance.
          </p>
        </div>

        <div className="p-5 bg-gradient-to-b from-[#141718] to-[#0E1011] border border-purple-500/30 rounded-2xl space-y-3">
          <div className="flex items-center gap-2 text-purple-400 font-bold text-xs uppercase tracking-wider">
            <ShieldCheck size={15} />
            <span>Clean Bootstrapping</span>
          </div>
          <div className="text-white text-sm font-semibold">
            100% Clean Cap Table
          </div>
          <p className="text-xs text-gray-400 leading-relaxed">
            Bootstrapped since 2023 with zero debt and zero outside equity dilution. Live production software actively dogfooded for internal corporate treasury.
          </p>
        </div>
      </div>

      {/* Screen Interactive Preview */}
      <div className="p-6 bg-[#0E1011] border border-[#242A2D] rounded-2xl space-y-6">
        <div className="flex items-center justify-between border-b border-[#222729] pb-4">
          <div>
            <h4 className="text-base font-bold text-white">Raksha Singh — Founder Dossier Preview</h4>
            <p className="text-xs text-gray-400 mt-0.5">Below is the exact A4 layout prepared for institutional accelerators and investors.</p>
          </div>
          <a
            href="https://azuraos.app"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-[#14B8A6] hover:underline flex items-center gap-1 font-semibold"
          >
            <span>Live Platform: azuraos.app</span>
            <ExternalLink size={12} />
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs text-gray-300">
          <div className="space-y-4">
            <div>
              <div className="text-amber-400 font-bold uppercase text-[10px] tracking-wider mb-1">Founder Journey &amp; Discipline</div>
              <p className="leading-relaxed text-gray-300">
                Bridges <strong>Systems Analysis</strong> (fault-tolerant protocol design and uncompromising discipline), <strong>Luxury Operations</strong> (white-glove executive UX and friction reduction), and <strong>Full-Stack Software Engineering</strong>. Built AzuraOS out of existential necessity to run a sovereign, global technology company lean without back-office bloat.
              </p>
            </div>

            <div>
              <div className="text-teal-400 font-bold uppercase text-[10px] tracking-wider mb-1">Key Technical Capabilities</div>
              <ul className="space-y-1.5 list-disc list-inside text-gray-400">
                <li><strong className="text-gray-200">Local-First Vault:</strong> Web Cryptography API (AES-GCM client-side encryption).</li>
                <li><strong className="text-gray-200">Sub-50ms Parser:</strong> Zero third-party bank credential harvesting (anti-Plaid liability).</li>
                <li><strong className="text-gray-200">Full-Stack Stack:</strong> React 19, TypeScript, Node.js, Express, Firebase Firestore &amp; Auth, Google Cloud Run.</li>
                <li><strong className="text-gray-200">AI Architecture:</strong> Gemini 2.5/3 SDK, BYOK routing (Claude 3.5 Sonnet, GPT-4o), Live Audio.</li>
              </ul>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <div className="text-emerald-400 font-bold uppercase text-[10px] tracking-wider mb-1">Commercial Traction &amp; Unit Economics</div>
              <ul className="space-y-1.5 list-disc list-inside text-gray-400">
                <li><strong className="text-gray-200">B2B Cohort Wedge:</strong> $750 for 30-day cohort pilot (25 founder seats + Director governance portal).</li>
                <li><strong className="text-gray-200">Defended Margins:</strong> &gt;85% gross margins via local-first computing and lean Cloud Run microservices.</li>
                <li><strong className="text-gray-200">LTV:CAC Ratio:</strong> ~7.6x via programmatic B2B2C cohort onboarding.</li>
                <li><strong className="text-gray-200">Global MoR:</strong> Dodo Payments operational for automated multi-country tax remittance.</li>
              </ul>
            </div>

            <div>
              <div className="text-purple-400 font-bold uppercase text-[10px] tracking-wider mb-1">Contact &amp; Corporate Identity</div>
              <p className="text-gray-400 leading-relaxed">
                <strong className="text-gray-200">Company:</strong> Azura Seraph™ Pty Ltd<br />
                <strong className="text-gray-200">Founder Email:</strong> founder@azuraos.app / iwillriseupstudio@gmail.com<br />
                <strong className="text-gray-200">Live Production:</strong> https://azuraos.app<br />
                <strong className="text-gray-200">Cap Table:</strong> 100% Founder Owned • Clean Pre-Seed Opportunity ($250k–$500k)
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 📄 CALIBRATED OFF-SCREEN CANVAS FOR 1-PAGE A4 PDF EXPORT */}
      <div 
        id="azura-founder-profile-pdf-wrapper"
        className="fixed -left-[9999px] -top-[9999px] overflow-hidden pointer-events-none"
        aria-hidden="true"
      >
        <div
          id="azura-founder-profile-a4-page"
          style={{
            width: '210mm',
            height: '297mm',
            maxHeight: '297mm',
            boxSizing: 'border-box',
            padding: '14mm 16mm',
            backgroundColor: '#ffffff',
            color: '#0f172a',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}
        >
          {/* Header */}
          <div style={{ borderBottom: '2.5px solid #d97706', paddingBottom: '10px' }} className="flex justify-between items-start">
            <div>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#0f172a', letterSpacing: '-0.3px' }}>
                RAKSHA SINGH — FOUNDER PROFILE
              </div>
              <div style={{ fontSize: '11px', color: '#d97706', fontWeight: '700', marginTop: '2px' }}>
                Founder &amp; Principal Architect • Azura Seraph™ Pty Ltd // AzuraOS
              </div>
              <div style={{ fontSize: '9px', color: '#64748b', marginTop: '3px' }}>
                Email: founder@azuraos.app | iwillriseupstudio@gmail.com • Live System: https://azuraos.app
              </div>
            </div>
            <div style={{ textAlign: 'right', fontSize: '9px', color: '#64748b', lineHeight: '1.4' }}>
              <div><strong>Status:</strong> 100% Bootstrapped</div>
              <div><strong>Cap Table:</strong> Clean (Zero Debt)</div>
              <div><strong>Google for Startups</strong> Recipient</div>
              <div><strong>Google Cloud Partner</strong> Member</div>
            </div>
          </div>

          {/* Section 1: Executive Summary */}
          <div>
            <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#0f172a', textTransform: 'uppercase', marginBottom: '4px', borderBottom: '1px solid #cbd5e1', paddingBottom: '2px' }}>
              1. Executive Summary &amp; Founder Archetype
            </div>
            <p style={{ fontSize: '9.5px', color: '#334155', lineHeight: '1.45', margin: '0' }}>
              Raksha Singh is the Founder and Principal Systems Architect behind AzuraOS and Azura Seraph™ Pty Ltd. Operating as a sovereign technical founder, she engineered and shipped AzuraOS end-to-end to solve the #1 failure vector of early-stage startups: cash flow exhaustion and unreserved tax liabilities. Her background uniquely unifies <strong>Systems Analysis &amp; Precision</strong> (fault-tolerant protocol design), <strong>Luxury Operations</strong> (frictionless executive UX), and <strong>Full-Stack Software Engineering</strong>.
            </p>
          </div>

          {/* Section 2: Core Technical & Architectural Stack */}
          <div>
            <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#0f172a', textTransform: 'uppercase', marginBottom: '6px', borderBottom: '1px solid #cbd5e1', paddingBottom: '2px' }}>
              2. Core Technical Architecture &amp; Engineering Mastery
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <div style={{ backgroundColor: '#f8fafc', padding: '7px 9px', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '9px' }}>
                <strong style={{ color: '#0f172a', display: 'block', marginBottom: '3px' }}>Client-Side Vault &amp; Air-Gapped Parser:</strong>
                <span style={{ color: '#475569', lineHeight: '1.4' }}>
                  Web Cryptography API (AES-GCM client-side encryption) and in-memory parser. Processes complex PDF/CSV statements in <strong>sub-50ms</strong> client-side with <strong>0% third-party bank credential scraping</strong> (anti-Plaid liability).
                </span>
              </div>
              <div style={{ backgroundColor: '#f8fafc', padding: '7px 9px', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '9px' }}>
                <strong style={{ color: '#0f172a', display: 'block', marginBottom: '3px' }}>Cloud Runtime &amp; Multi-Jurisdiction Engine:</strong>
                <span style={{ color: '#475569', lineHeight: '1.4' }}>
                  React 19, TypeScript, Node.js, Express, Firebase Firestore &amp; Auth, Google Cloud Run. Real-time tax reserve engine calibrated for US (IRS), UK (HMRC), South Africa (SARS), EU (VAT), and India (GST).
                </span>
              </div>
              <div style={{ backgroundColor: '#f8fafc', padding: '7px 9px', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '9px' }}>
                <strong style={{ color: '#0f172a', display: 'block', marginBottom: '3px' }}>Multimodal AI &amp; BYOK Architecture:</strong>
                <span style={{ color: '#475569', lineHeight: '1.4' }}>
                  Direct Google Gemini 2.5/3 Pro &amp; Flash integration via @google/genai SDK with BYOK failovers to Claude 3.5 Sonnet and GPT-4o. Multimodal voice synthesis for executive co-founder simulations.
                </span>
              </div>
              <div style={{ backgroundColor: '#f8fafc', padding: '7px 9px', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '9px' }}>
                <strong style={{ color: '#0f172a', display: 'block', marginBottom: '3px' }}>Global Billing &amp; Settlement:</strong>
                <span style={{ color: '#475569', lineHeight: '1.4' }}>
                  Integrated Dodo Payments Merchant of Record handling automated multi-currency cross-border tax remittance and SaaS recurring subscriptions across 150+ countries.
                </span>
              </div>
            </div>
          </div>

          {/* Section 3: Verified Ecosystem Backing & Credentials */}
          <div>
            <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#0f172a', textTransform: 'uppercase', marginBottom: '4px', borderBottom: '1px solid #cbd5e1', paddingBottom: '2px' }}>
              3. Institutional Ecosystem Credentials &amp; Industry Backing
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px', textAlign: 'left' }}>
              <tbody>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '4px 6px', fontWeight: 'bold', width: '35%', color: '#0f172a' }}>Google for Startups Cloud Program</td>
                  <td style={{ padding: '4px 6px', color: '#475569' }}>Start Tier Grant Recipient ($2,000 developer cloud infrastructure allocation).</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '4px 6px', fontWeight: 'bold', color: '#0f172a' }}>Google Cloud Partner Advantage</td>
                  <td style={{ padding: '4px 6px', color: '#475569' }}>Validated Member status with technical architecture alignment.</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '4px 6px', fontWeight: 'bold', color: '#0f172a' }}>AltaLab Sprint 2026</td>
                  <td style={{ padding: '4px 6px', color: '#475569' }}>Selected for active founder sprint cohort.</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '4px 6px', fontWeight: 'bold', color: '#0f172a' }}>Slush 2026 (Helsinki)</td>
                  <td style={{ padding: '4px 6px', color: '#475569' }}>Accepted founder profile alongside top 25 global venture capital firms.</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '4px 6px', fontWeight: 'bold', color: '#0f172a' }}>WIT-h Global Network</td>
                  <td style={{ padding: '4px 6px', color: '#475569' }}>Formally inducted member of the Women in Tech global network.</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Section 4: Unit Economics & Commercial Traction */}
          <div>
            <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#0f172a', textTransform: 'uppercase', marginBottom: '4px', borderBottom: '1px solid #cbd5e1', paddingBottom: '2px' }}>
              4. Commercial Traction, GTM Wedge &amp; Unit Economics
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px', textAlign: 'center', marginBottom: '6px' }}>
              <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a', padding: '6px', borderRadius: '4px' }}>
                <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#b45309' }}>$750</div>
                <div style={{ fontSize: '8px', color: '#78350f' }}>Cohort Pilot (25 Seats)</div>
              </div>
              <div style={{ backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0', padding: '6px', borderRadius: '4px' }}>
                <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#047857' }}>&gt;85%</div>
                <div style={{ fontSize: '8px', color: '#064e3b' }}>Defended Gross Margin</div>
              </div>
              <div style={{ backgroundColor: '#f0fdfa', border: '1px solid #99f6e4', padding: '6px', borderRadius: '4px' }}>
                <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#0f766e' }}>~7.6x</div>
                <div style={{ fontSize: '8px', color: '#134e4a' }}>Target LTV:CAC Ratio</div>
              </div>
              <div style={{ backgroundColor: '#f5f3ff', border: '1px solid #ddd6fe', padding: '6px', borderRadius: '4px' }}>
                <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#6d28d9' }}>15+</div>
                <div style={{ fontSize: '8px', color: '#4c1d95' }}>Private Tester Operators</div>
              </div>
            </div>
            <p style={{ fontSize: '9px', color: '#475569', lineHeight: '1.4', margin: '0' }}>
              <strong>The B2B2C Accelerator Wedge:</strong> Selling 30-day cohort licenses ($750–$1,250/batch) to accelerators provides immediate cash flow, eliminates high direct ad acquisition costs, and provisions 25 active founders per cohort who transition into recurring post-grad SaaS subscribers ($29–$79/mo).
            </p>
          </div>

          {/* Section 5: Founder Commitment & Capital Strategy */}
          <div>
            <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#0f172a', textTransform: 'uppercase', marginBottom: '4px', borderBottom: '1px solid #cbd5e1', paddingBottom: '2px' }}>
              5. Founder Commitment &amp; Pre-Seed Strategy
            </div>
            <div style={{ backgroundColor: '#f8fafc', border: '1px solid #cbd5e1', padding: '8px 10px', borderRadius: '4px', fontSize: '9px', color: '#334155', lineHeight: '1.45' }}>
              <div>• <strong>Commitment:</strong> 100% Full-time dedicated operational focus with clean, unencumbered intellectual property.</div>
              <div>• <strong>Cap Table:</strong> 100% Founder-owned with zero debt, zero warrants, and zero SAFE overhang.</div>
              <div>• <strong>Target Round:</strong> $250,000 – $500,000 Pre-Seed Round to scale cohort partnership acquisitions and cloud distribution.</div>
            </div>
          </div>

          {/* Footer */}
          <div style={{ borderTop: '1px solid #cbd5e1', paddingTop: '6px', fontSize: '8px', color: '#64748b', display: 'flex', justifyContent: 'space-between' }}>
            <span>Azura Seraph™ Pty Ltd • Confidential Founder Briefing</span>
            <span>Direct: founder@azuraos.app • https://azuraos.app</span>
            <span>Page 1 of 1</span>
          </div>
        </div>
      </div>
    </div>
  );
}
